/**
 * EmailTemplateTranslateModal — "Proponi traduzioni (AI)".
 * Prende i testi personalizzati salvati nella lingua corrente e genera
 * proposte AI per le altre lingue. NIENTE viene salvato in automatico:
 * Mara rivede ogni lingua e la salva esplicitamente, una per una.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Languages, Loader2, Save, Check, ChevronDown, ChevronRight, AlertCircle } from "lucide-react";
import { API } from "../../config";

export const EmailTemplateTranslateModal = ({ emailType, sourceCountry, availableCountries, fieldsMeta, onClose, onSaved }) => {
  const targets = (availableCountries || []).filter(c => c.code !== sourceCountry);
  const [selected, setSelected] = useState(() => new Set(targets.map(c => c.code)));
  const [generating, setGenerating] = useState(false);
  const [proposals, setProposals] = useState(null); // {code: {field: text}}
  const [errors, setErrors] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [savedLangs, setSavedLangs] = useState(new Set());
  const [savingLang, setSavingLang] = useState(null);

  const toggle = (code) => setSelected(s => {
    const n = new Set(s);
    if (n.has(code)) n.delete(code); else n.add(code);
    return n;
  });

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/email-templates/${emailType}/propose-translations`, {
        source_country: sourceCountry,
        target_countries: [...selected],
      });
      setProposals(r.data.proposals || {});
      setErrors(r.data.errors || {});
      const codes = Object.keys(r.data.proposals || {});
      if (codes.length > 0) setExpanded(codes[0]);
      toast.success(`Proposte generate per ${codes.length} lingue`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generazione proposte fallita");
    } finally {
      setGenerating(false);
    }
  };

  const saveLang = async (code) => {
    setSavingLang(code);
    try {
      await axios.put(`${API}/admin/email-templates/${emailType}`, {
        country: code,
        fields: proposals[code],
      });
      setSavedLangs(s => new Set([...s, code]));
      toast.success(`Testi ${code} salvati`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Salvataggio ${code} fallito`);
    } finally {
      setSavingLang(null);
    }
  };

  const labelFor = (key) => fieldsMeta?.find(f => f.key === key)?.label || key;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => !generating && onClose()} data-testid="translate-modal">
      <div className="bg-[#0a0a0a] border border-gray-800 rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-800">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Languages size={16} className="text-lojack-blue-400" />
            Proponi traduzioni (AI) — da {sourceCountry}
          </h3>
          <button onClick={onClose} disabled={generating} className="text-gray-500 hover:text-white" data-testid="translate-modal-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {!proposals && (
            <>
              <p className="text-gray-400 text-xs mb-3 leading-relaxed">
                L&apos;AI tradurrà i testi personalizzati salvati in <strong className="text-white">{sourceCountry}</strong> nelle
                lingue selezionate. <strong className="text-lojack-clause-200">Nulla viene salvato automaticamente</strong>:
                potrai rivedere e salvare ogni lingua singolarmente.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 mb-4">
                {targets.map(({ code, label }) => (
                  <label key={code} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border cursor-pointer text-xs transition ${selected.has(code) ? "border-lojack-blue-500/50 bg-lojack-blue-500/10 text-white" : "border-gray-800 text-gray-500 hover:border-gray-600"}`}>
                    <input type="checkbox" checked={selected.has(code)} onChange={() => toggle(code)} className="accent-lojack-blue-500" data-testid={`translate-target-${code}`} />
                    <span className="font-mono">{code}</span>
                    <span className="truncate">{label}</span>
                  </label>
                ))}
              </div>
              <div className="flex items-center gap-2 text-[10px] text-gray-500 mb-1">
                <button onClick={() => setSelected(new Set(targets.map(c => c.code)))} className="underline hover:text-white">Seleziona tutte</button>
                <button onClick={() => setSelected(new Set())} className="underline hover:text-white">Deseleziona tutte</button>
              </div>
            </>
          )}

          {proposals && (
            <div className="space-y-2">
              {Object.keys(errors).length > 0 && (
                <p className="text-lojack-red text-xs flex items-center gap-1.5" data-testid="translate-errors">
                  <AlertCircle size={12} /> Lingue non tradotte: {Object.keys(errors).join(", ")}
                </p>
              )}
              {Object.entries(proposals).map(([code, fields]) => (
                <div key={code} className="border border-gray-800 rounded-xl overflow-hidden" data-testid={`translate-proposal-${code}`}>
                  <button
                    onClick={() => setExpanded(e => e === code ? null : code)}
                    className="w-full flex items-center justify-between px-3 py-2.5 bg-[#111] hover:bg-[#161616] transition"
                  >
                    <span className="text-sm text-white flex items-center gap-2">
                      {expanded === code ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <span className="font-mono text-lojack-blue-400">{code}</span>
                      {availableCountries?.find(c => c.code === code)?.label}
                    </span>
                    {savedLangs.has(code) ? (
                      <span className="text-emerald-400 text-xs flex items-center gap-1"><Check size={12} /> salvata</span>
                    ) : (
                      <span className="text-gray-500 text-[10px]">proposta — da rivedere</span>
                    )}
                  </button>
                  {expanded === code && (
                    <div className="p-3 space-y-2.5 bg-[#0d0d0d]">
                      {Object.entries(fields).map(([key, value]) => (
                        <div key={key}>
                          <label className="text-gray-500 text-[10px] uppercase tracking-wide">{labelFor(key)}</label>
                          <textarea
                            value={value}
                            onChange={e => setProposals(p => ({ ...p, [code]: { ...p[code], [key]: e.target.value } }))}
                            rows={2}
                            className="w-full mt-1 bg-black border border-gray-700 rounded-lg px-2.5 py-1.5 text-xs text-white focus:border-lojack-blue-500 outline-none resize-y"
                            data-testid={`translate-field-${code}-${key}`}
                          />
                        </div>
                      ))}
                      <button
                        onClick={() => saveLang(code)}
                        disabled={savingLang === code || savedLangs.has(code)}
                        className="w-full bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 rounded-lg px-3 py-2 text-xs font-semibold flex items-center justify-center gap-1.5 transition"
                        data-testid={`translate-save-${code}`}
                      >
                        {savingLang === code ? <Loader2 size={12} className="animate-spin" /> : savedLangs.has(code) ? <Check size={12} /> : <Save size={12} />}
                        {savedLangs.has(code) ? "Salvata" : `Salva testi ${code}`}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} disabled={generating} className="px-4 py-2 text-gray-400 hover:text-white text-sm transition" data-testid="translate-modal-cancel">
            Chiudi
          </button>
          {!proposals && (
            <button
              onClick={generate}
              disabled={generating || selected.size === 0}
              className="px-4 py-2 bg-lojack-blue-600 hover:bg-lojack-blue-700 disabled:opacity-50 text-white rounded-xl text-sm font-medium flex items-center gap-2 transition"
              data-testid="translate-generate-btn"
            >
              {generating ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
              {generating ? `Traduzione in corso (${selected.size} lingue)...` : `Genera proposte AI (${selected.size} lingue)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateTranslateModal;
